import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http, formatApiErrorDetail, trackEvent, setInternalTraffic } from "@/lib/api";

const AuthContext = createContext(null);

// Owner/admin/operator and flagged test accounts are internal traffic — kept out of GA4 reports.
function markInternal(user) {
  const internal = !!user && (["admin", "operator"].includes(user.role) || user.test === true);
  setInternalTraffic(internal);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, obj = user

  const refresh = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
      markInternal(data);
      return data;
    } catch (e) {
      setUser(false);
      markInternal(false);
      return false;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await http.post("/auth/login", { email, password });
    setUser(data);
    markInternal(data);
    trackEvent("login", { method: "password", role: data.role });
    return data;
  };
  const register = async (email, password, name) => {
    const { data } = await http.post("/auth/register", { email, password, name });
    setUser(data);
    markInternal(data);
    trackEvent("sign_up", { method: "password", role: data.role });
    return data;
  };
  const logout = async () => {
    try { await http.post("/auth/logout"); } catch (e) {}
    setUser(false);
    markInternal(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiErrorDetail };
